/* eslint-disable react-hooks/exhaustive-deps */
import { VaAlert } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { scrollAndFocus } from 'platform/utilities/scroll';
import { focusElement } from 'platform/utilities/ui/focus';
import { slugifyText } from './helpers';

const SuccessAlert = ({ nounSingular, index, onDismiss, text }) => (
  <div className="vads-u-margin-top--2">
    <VaAlert
      onCloseEvent={onDismiss}
      slim
      closeable
      name={`${slugifyText(nounSingular)}_${index}`}
      status="success"
      closeBtnAriaLabel="Close notification"
      uswds
    >
      <div className="dd-privacy-mask" data-dd-action-name="Success Alert">
        {text}
      </div>
    </VaAlert>
  </div>
);

const MaxItemsAlert = forwardRef(({ children, show }, ref) =>
  show ? (
    <div className="vads-u-margin-top--4">
      <va-alert slim status="warning" tabIndex={-1} visible ref={ref}>
        <div className="vads-u-margin-y--0 vads-u-font-weight--normal">
          {children}
        </div>
      </va-alert>
    </div>
  ) : null,
);

function getYesNoReviewErrorMessage(reviewErrors, hasItemsKey) {
  // use the same error message as the yes/no field
  const error =
    hasItemsKey && reviewErrors?.errors?.find(obj => obj.name === hasItemsKey);
  return error?.message;
}

/**
 * Alert state, refs, effects, and rendering for ArrayBuilderSummaryPage.
 * The page keeps ownership of `renderKey`/`hasVisibleDescriptionContent`, so
 * this returns a `renderAlerts` render function plus the individual show flags
 * it reads. `renderAlerts` is a plain function (not a component) so the alert
 * subtree reconciles in place instead of remounting on every page render.
 *
 * @param {Object} options
 * @param {ArrayBuilderOptions} options.arrayBuilderOptions
 * @param {CustomPageProps} options.customPageProps
 * @param {boolean} options.isMaxItemsReached
 * @param {Array} options.arrayData
 * @param {Object|null} options.updatedItemData
 * @param {number|null} options.updateItemIndex
 * @param {string} options.updatedArrayPathSlug
 * @param {string} options.arrayPathSlug
 * @param {boolean} options.hasReviewError
 * @param {() => boolean} options.getMaxItemsAlertSeen - read the page's
 *   `maxItemsAlertSeen` closure variable
 * @param {(seen: boolean) => void} options.setMaxItemsAlertSeen - write it
 */
export function useSummaryPageAlerts({
  arrayBuilderOptions,
  customPageProps: props,
  isMaxItemsReached,
  arrayData,
  updatedItemData,
  updateItemIndex,
  updatedArrayPathSlug,
  arrayPathSlug,
  hasReviewError,
  getMaxItemsAlertSeen,
  setMaxItemsAlertSeen,
}) {
  const {
    arrayPath,
    getText,
    hasItemsKey,
    hideMaxItemsAlert,
    nounPlural,
    nounSingular,
    useLinkInsteadOfYesNo,
    useButtonInsteadOfYesNo,
  } = arrayBuilderOptions;

  const [showUpdatedAlert, setShowUpdatedAlert] = useState(!!updatedItemData);
  const [showRemovedAlert, setShowRemovedAlert] = useState(false);
  const [showReviewErrorAlert, setShowReviewErrorAlert] = useState(false);
  const [removedItemText, setRemovedItemText] = useState('');
  const [removedItemIndex, setRemovedItemIndex] = useState(null);
  const updatedAlertRef = useRef(null);
  const removedAlertRef = useRef(null);
  const reviewErrorAlertRef = useRef(null);
  const maxItemsAlertRef = useRef(null);

  const getTitleSelector = () =>
    `[data-title-for-noun-singular="${nounSingular}"][data-array-path="${arrayPath}"]`;

  useEffect(() => {
    if (updatedArrayPathSlug === arrayPathSlug) {
      setShowUpdatedAlert(() => updateItemIndex != null);
    }
  }, [updatedArrayPathSlug, updateItemIndex, arrayPathSlug]);

  useEffect(() => {
    let timeout;
    let focusRef;

    if (
      showUpdatedAlert &&
      updateItemIndex != null &&
      updatedAlertRef.current
    ) {
      focusRef = updatedAlertRef;
    } else if (
      isMaxItemsReached &&
      !getMaxItemsAlertSeen() &&
      maxItemsAlertRef
    ) {
      focusRef = maxItemsAlertRef;
    }

    setMaxItemsAlertSeen(isMaxItemsReached);

    if (focusRef) {
      timeout = setTimeout(() => {
        if (focusRef.current) {
          scrollAndFocus(focusRef.current);
        }
      }, 300); // need to wait to override pageScrollAndFocus
    }

    return () => timeout && clearTimeout(timeout);
  }, [showUpdatedAlert, updateItemIndex, updatedAlertRef, isMaxItemsReached]);

  useEffect(() => {
    setShowReviewErrorAlert(hasReviewError);
    if (
      props.recalculateErrors &&
      props.name &&
      (showUpdatedAlert || showRemovedAlert)
    ) {
      // Affects the red highlighting at the chapter level and
      // error messages. This prop only exists on the review page.
      props.recalculateErrors(props.name);
    }
  }, [hasReviewError, showUpdatedAlert, showRemovedAlert]);

  function onDismissUpdatedAlert() {
    setShowUpdatedAlert(false);
    requestAnimationFrame(() => {
      focusElement(document.querySelector(getTitleSelector()));
    });
  }

  function onDismissRemovedAlert() {
    setShowRemovedAlert(false);
    setRemovedItemText('');
    setRemovedItemIndex(null);
    requestAnimationFrame(() => {
      focusElement(document.querySelector(getTitleSelector()));
    });
  }

  function showRemovedItemAlert(text, index) {
    // updated alert may be from initial state (URL path)
    // so we can go ahead and remove it if there is a new
    // alert
    setShowUpdatedAlert(false);
    setRemovedItemText(text);
    setRemovedItemIndex(index);
    setShowRemovedAlert(true);
    requestAnimationFrame(() => {
      focusElement(removedAlertRef.current);
    });
  }

  const renderUpdatedAlert = () => (
    <div ref={updatedAlertRef}>
      {showUpdatedAlert ? (
        <SuccessAlert
          onDismiss={onDismissUpdatedAlert}
          nounSingular={nounSingular}
          index={updateItemIndex}
          text={getText(
            'alertItemUpdated',
            updatedItemData,
            props.data,
            updateItemIndex,
          )}
        />
      ) : null}
    </div>
  );

  const renderRemovedAlert = () => (
    <div ref={removedAlertRef}>
      {showRemovedAlert ? (
        <SuccessAlert
          onDismiss={onDismissRemovedAlert}
          nounSingular={nounSingular}
          index={removedItemIndex}
          text={removedItemText}
        />
      ) : null}
    </div>
  );

  const renderReviewErrorAlert = () => (
    <div ref={reviewErrorAlertRef}>
      {showReviewErrorAlert ? (
        <div className="vads-u-margin-top--2">
          <VaAlert
            status="error"
            slim
            tabIndex={0}
            visible
            name={`${nounPlural}ReviewError`}
          >
            {getYesNoReviewErrorMessage(props.reviewErrors, hasItemsKey)}
          </VaAlert>
        </div>
      ) : null}
    </div>
  );

  const renderAlerts = () => {
    const showMaxItemsAlert = isMaxItemsReached && !hideMaxItemsAlert;
    const alertsShown =
      showMaxItemsAlert ||
      showUpdatedAlert ||
      showRemovedAlert ||
      showReviewErrorAlert;
    const isButtonOrLink = useLinkInsteadOfYesNo || useButtonInsteadOfYesNo;

    return (
      <div
        className={
          alertsShown && isButtonOrLink && !arrayData?.length
            ? 'vads-u-margin-bottom--4'
            : ''
        }
      >
        <MaxItemsAlert show={showMaxItemsAlert} ref={maxItemsAlertRef}>
          {getText(
            'alertMaxItems',
            updatedItemData,
            props.data,
            updateItemIndex,
          )}
        </MaxItemsAlert>
        {renderRemovedAlert()}
        {renderUpdatedAlert()}
        {renderReviewErrorAlert()}
      </div>
    );
  };

  return {
    renderAlerts,
    showUpdatedAlert,
    showRemovedAlert,
    showReviewErrorAlert,
    showRemovedItemAlert,
  };
}

SuccessAlert.propTypes = {
  index: PropTypes.number,
  nounSingular: PropTypes.string,
  text: PropTypes.string,
  onDismiss: PropTypes.func,
};

MaxItemsAlert.propTypes = {
  children: PropTypes.node,
  show: PropTypes.bool,
};
